import React, { RefObject } from "react";
import { createPortal } from "react-dom";
import useAnchoredPanel, { anchoredPanelStyle } from "./useAnchoredPanel";

/**
 * Every answer a row is only showing part of, listed one to a line under it.
 *
 * What the "+4" on a one-line list is for: the count says how much is missing,
 * and this says what. Shown on hover by both places that count — the allergy
 * picker in the client forms, and the allergies as the Details tab reads them
 * back — so the same answers are opened the same way wherever they are met.
 *
 * A box and not a `title`: the browser draws that as one long line, which is a
 * dozen allergies in a strip wider than the form itself. One answer to a line
 * is the point of it, since the ones behind the count are exactly what a single
 * wrapped line reads worst.
 *
 * Portalled to the body and positioned against the anchor, the way every other
 * floating panel here is — see `useAnchoredPanel` for why a panel that belongs
 * to a scroll container cannot simply be absolutely positioned inside it.
 */

/** Tallest the list can get before it scrolls. */
export const TOOLTIP_MAX_HEIGHT = 200;

export interface OverflowTooltipProps {
  /** What it is drawn under — the row whose answers it is listing. */
  anchorRef: RefObject<HTMLElement | null>;
  open: boolean;
  /** Every answer, not only the ones behind the count. */
  items: string[];
  /** The line above them: "4 selected", "4 food allergies". */
  heading: string;
}

const OverflowTooltip: React.FC<OverflowTooltipProps> = ({
  anchorRef,
  open,
  items,
  heading,
}) => {
  const { rect, openUpward } = useAnchoredPanel(
    anchorRef,
    open,
    TOOLTIP_MAX_HEIGHT
  );

  if (!open || !rect) return null;

  return createPortal(
    <div
      role="tooltip"
      // Takes no pointer events: it is drawn under the row it belongs to, and
      // a box that could be hovered would flicker on its own edge.
      className="pointer-events-none z-[70] overflow-y-auto rounded-md border border-line bg-white px-3 py-2 shadow-[0_8px_24px_rgba(11,27,31,0.12)] custom-scrollbar-overlay"
      style={anchoredPanelStyle(rect, openUpward, TOOLTIP_MAX_HEIGHT)}
    >
      <p className="mb-1 text-[11px] uppercase tracking-wide text-ink-subtle">
        {heading}
      </p>
      <ul className="space-y-0.5">
        {items.map((item, index) => (
          <li
            key={`${item}-${index}`}
            className="text-[13px] leading-snug text-ink"
          >
            {item}
          </li>
        ))}
      </ul>
    </div>,
    document.body
  );
};

export default OverflowTooltip;
